import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { motion } from 'framer-motion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import { ease } from '../motion';
import { profile } from '../content';
import Typewriter from './Typewriter';
import MagneticButton from './MagneticButton';

const Hero: React.FC = () => {
  const reveal = (delay: number) => ({
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { delay, duration: 0.8, ease: ease.out },
  });

  return (
    <Box
      component="section"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 980, py: { xs: 14, md: 10 } }}>
          {/* eyebrow */}
          <motion.div {...reveal(0.1)}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                mb: { xs: 3, md: 4 },
                px: 2,
                py: 0.75,
                borderRadius: 999,
                border: (t) => `1px solid ${t.palette.divider}`,
                backgroundColor: 'rgba(255,255,255,0.02)',
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  boxShadow: (t) => `0 0 12px ${t.palette.primary.main}`,
                }}
              />
              <Typography
                sx={{
                  fontFamily: (t) => t.typography.button.fontFamily,
                  fontSize: '0.75rem',
                  letterSpacing: '0.08em',
                  color: 'text.secondary',
                }}
              >
                {profile.role} · {profile.location}
              </Typography>
            </Box>
          </motion.div>

          {/* headline */}
          <motion.div {...reveal(0.2)}>
            <Typography
              variant="h1"
              component="h1"
              sx={{ color: 'text.primary', mb: { xs: 3, md: 4 } }}
            >
              {profile.headline[0]}
              <br />
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {profile.headline[1].split(' ').slice(0, -1).join(' ')}{' '}
              </Box>
              <Box
                component="span"
                sx={{
                  color: 'primary.main',
                  position: 'relative',
                  whiteSpace: 'nowrap',
                }}
              >
                {profile.headline[1].split(' ').slice(-1)}
                <Box
                  component="span"
                  sx={{ color: 'primary.main', WebkitTextFillColor: 'currentColor' }}
                >
                  .
                </Box>
              </Box>
            </Typography>
          </motion.div>

          {/* typed terminal line */}
          <motion.div {...reveal(0.35)}>
            <Box sx={{ mb: { xs: 4, md: 5 }, fontSize: { xs: '0.95rem', md: '1.1rem' } }}>
              <Typewriter lines={profile.typedLines} />
            </Box>
          </motion.div>

          {/* intro */}
          <motion.div {...reveal(0.5)}>
            <Typography
              variant="body1"
              sx={{ maxWidth: 620, color: 'text.secondary', mb: { xs: 5, md: 6 } }}
            >
              {profile.intro}
            </Typography>
          </motion.div>

          {/* CTAs */}
          <motion.div {...reveal(0.65)}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <MagneticButton
                variant="contained"
                endIcon={<NorthEastIcon sx={{ fontSize: '1rem !important' }} />}
                onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
              >
                View my work
              </MagneticButton>
              <MagneticButton
                variant="outlined"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Get in touch
              </MagneticButton>
            </Box>
          </motion.div>
        </Box>
      </Container>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
        style={{
          position: 'absolute',
          bottom: 28,
          left: '50%',
          transform: 'translateX(-50%)',
          cursor: 'pointer',
        }}
        onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}>
          <KeyboardArrowDownIcon sx={{ color: 'text.secondary', fontSize: '1.6rem' }} />
        </motion.div>
      </motion.div>
    </Box>
  );
};

export default Hero;
